import { faker } from '@faker-js/faker'

export interface LeadTableRow {
  id: string
  timestamp: Date
  source: 'everflow' | 'fluent' | 'manual'
  
  // Contact Details
  firstName: string
  lastName: string
  email: string
  phone: string
  zip: string
  
  // Technical Data
  ip: string
  ipType: 'residential' | 'datacenter' | 'vpn' | 'proxy'
  userAgent: string
  referrer: string
  
  // Risk Analysis
  riskScore: number
  riskFactors: RiskFactor[]
  mlConfidence: number
  
  // Fraud Indicators
  patterns: {
    sequentialPhone: boolean
    suspiciousEmail: boolean
    datacenterIP: boolean
    velocityAttack: boolean
    zipConcentration: boolean
  }
  
  // Status
  status: 'pending' | 'approved' | 'rejected'
  reviewedBy?: string
  reviewedAt?: Date
  notes?: string
  costImpact: number
}

export interface RiskFactor {
  type: string
  weight: number
  description: string
  severity: 'low' | 'medium' | 'high' | 'critical'
}

export interface FilterState {
  dateRange: 'today' | 'yesterday' | 'week' | 'month' | 'custom'
  riskLevel: 'all' | 'low' | 'medium' | 'high' | 'critical'
  status: 'all' | 'pending' | 'approved' | 'rejected'
  source: 'all' | 'everflow' | 'fluent' | 'manual'
  pattern: 'all' | 'sequential' | 'velocity' | 'ip_cluster' | 'domain' | 'zip'
  search: string
}

const suspiciousDomains = [
  'armyspy.com', 'dayrep.com', 'rhyta.com', 'teleworm.us', 'jourrapide.com',
  'einrot.com', 'fleckens.hu', 'gustr.com', 'superrito.com', 'cuvox.de'
]

const fraudZips = ['80112', '90210', '10001', '60601', '33101']
const datacenterIPs = ['169.197.85', '212.102.60', '207.244.77']

function generateFraudulentLead(index: number): LeadTableRow {
  const basePhone = 2262433170
  const firstName = faker.person.firstName()
  const lastName = faker.person.lastName()
  
  const riskFactors: RiskFactor[] = []
  let riskScore = 0
  
  // Sequential phone pattern
  const isSequential = Math.random() > 0.3
  const phone = isSequential ? `${basePhone + (index % 20)}` : faker.phone.number()
  if (isSequential) {
    riskFactors.push({
      type: 'Sequential Phone',
      weight: 25,
      description: 'Phone number follows sequential pattern indicating automated submission',
      severity: 'high'
    })
    riskScore += 25
  }
  
  // Suspicious email domain
  const isSuspiciousEmail = Math.random() > 0.2
  const email = isSuspiciousEmail 
    ? `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${suspiciousDomains[index % suspiciousDomains.length]}`
    : faker.internet.email({ firstName, lastName })
  
  if (isSuspiciousEmail) {
    riskFactors.push({
      type: 'Suspicious Email Domain',
      weight: 15,
      description: 'Email domain associated with temporary/fake email services',
      severity: 'medium'
    })
    riskScore += 15
  }
  
  // Datacenter IP
  const isDatacenterIP = Math.random() > 0.4
  const ip = isDatacenterIP
    ? `${datacenterIPs[index % datacenterIPs.length]}.${Math.floor(Math.random() * 255)}`
    : faker.internet.ip()
    
  if (isDatacenterIP) {
    riskFactors.push({
      type: 'Data Center IP',
      weight: 20,
      description: 'IP address originates from known data center or VPS provider',
      severity: 'high'
    })
    riskScore += 20
  }
  
  // Zip concentration
  const isZipConcentration = Math.random() > 0.3
  const zip = isZipConcentration 
    ? fraudZips[index % fraudZips.length]
    : faker.location.zipCode()
    
  if (isZipConcentration) {
    riskFactors.push({
      type: 'Zip Code Concentration',
      weight: 10,
      description: 'High volume of leads from this zip code detected',
      severity: 'medium'
    })
    riskScore += 10
  }
  
  // Velocity attack
  const isVelocity = Math.random() > 0.8
  if (isVelocity) {
    riskFactors.push({
      type: 'Velocity Attack',
      weight: 20,
      description: 'Multiple submissions detected within short time window',
      severity: 'critical'
    })
    riskScore += 20
  }
  
  // ML prediction
  const mlWeight = Math.random() * 10
  riskFactors.push({
    type: 'ML Prediction',
    weight: mlWeight,
    description: `Machine learning model confidence: ${(mlWeight * 10).toFixed(1)}%`,
    severity: mlWeight > 7 ? 'high' : mlWeight > 4 ? 'medium' : 'low'
  })
  riskScore += mlWeight
  
  return {
    id: `lead-${Date.now()}-${index}`,
    timestamp: new Date(Date.now() - Math.random() * 86400000 * 7), // Last 7 days
    source: ['everflow', 'fluent', 'manual'][Math.floor(Math.random() * 3)] as any,
    firstName,
    lastName,
    email,
    phone,
    zip,
    ip,
    ipType: isDatacenterIP ? 'datacenter' : 'residential',
    userAgent: faker.internet.userAgent(),
    referrer: faker.internet.url(),
    riskScore: Math.min(Math.round(riskScore), 100),
    riskFactors,
    mlConfidence: 0.7 + Math.random() * 0.3,
    patterns: {
      sequentialPhone: isSequential,
      suspiciousEmail: isSuspiciousEmail,
      datacenterIP: isDatacenterIP,
      velocityAttack: isVelocity,
      zipConcentration: isZipConcentration
    },
    status: 'pending',
    costImpact: 30 // Cost saved if blocked
  }
}

function generateLegitimuteLead(index: number): LeadTableRow {
  const firstName = faker.person.firstName()
  const lastName = faker.person.lastName()
  
  const riskFactors: RiskFactor[] = [
    {
      type: 'ML Prediction',
      weight: Math.random() * 5,
      description: `Machine learning model confidence: ${(Math.random() * 50).toFixed(1)}%`,
      severity: 'low'
    }
  ]
  
  return {
    id: `lead-${Date.now()}-${index}`,
    timestamp: new Date(Date.now() - Math.random() * 86400000 * 7),
    source: ['everflow', 'fluent', 'manual'][Math.floor(Math.random() * 3)] as any,
    firstName,
    lastName,
    email: faker.internet.email({ firstName, lastName }),
    phone: faker.phone.number(),
    zip: faker.location.zipCode(),
    ip: faker.internet.ip(),
    ipType: 'residential',
    userAgent: faker.internet.userAgent(),
    referrer: faker.internet.url(),
    riskScore: Math.floor(Math.random() * 35), // 0-35 for legitimate
    riskFactors,
    mlConfidence: 0.3 + Math.random() * 0.4,
    patterns: {
      sequentialPhone: false,
      suspiciousEmail: false,
      datacenterIP: false,
      velocityAttack: false,
      zipConcentration: false
    },
    status: 'pending',
    costImpact: -150 // Revenue lost if incorrectly blocked
  }
}

export function generateMockLeads(count: number): LeadTableRow[] {
  const leads: LeadTableRow[] = []
  
  for (let i = 0; i < count; i++) {
    const isFraud = Math.random() > 0.68 // 32% fraud rate from real data
    
    if (isFraud) {
      leads.push(generateFraudulentLead(i))
    } else {
      leads.push(generateLegitimuteLead(i))
    }
  }
  
  return leads.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
}

export const defaultFilters: FilterState = {
  dateRange: 'today',
  riskLevel: 'all',
  status: 'all',
  source: 'all',
  pattern: 'all',
  search: ''
}

export function filterLeads(leads: LeadTableRow[], filters: FilterState): LeadTableRow[] {
  return leads.filter(lead => {
    // Date range filter
    const now = new Date()
    const leadDate = new Date(lead.timestamp)
    
    switch (filters.dateRange) {
      case 'today':
        if (leadDate.toDateString() !== now.toDateString()) return false
        break
      case 'yesterday':
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        if (leadDate.toDateString() !== yesterday.toDateString()) return false
        break
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        if (leadDate < weekAgo) return false
        break
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        if (leadDate < monthAgo) return false
        break
    }
    
    // Risk level filter
    if (filters.riskLevel !== 'all') {
      const score = lead.riskScore
      switch (filters.riskLevel) {
        case 'low':
          if (score > 30) return false
          break
        case 'medium':
          if (score <= 30 || score > 60) return false
          break
        case 'high':
          if (score <= 60 || score > 80) return false
          break
        case 'critical':
          if (score <= 80) return false
          break
      }
    }
    
    // Status filter
    if (filters.status !== 'all' && lead.status !== filters.status) {
      return false
    }
    
    // Source filter
    if (filters.source !== 'all' && lead.source !== filters.source) {
      return false
    }
    
    // Pattern filter
    if (filters.pattern !== 'all') {
      switch (filters.pattern) {
        case 'sequential':
          if (!lead.patterns.sequentialPhone) return false
          break
        case 'velocity':
          if (!lead.patterns.velocityAttack) return false
          break
        case 'ip_cluster':
          if (!lead.patterns.datacenterIP) return false
          break
        case 'domain':
          if (!lead.patterns.suspiciousEmail) return false
          break
        case 'zip':
          if (!lead.patterns.zipConcentration) return false
          break
      }
    }
    
    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase()
      const searchableText = [
        lead.firstName,
        lead.lastName,
        lead.email,
        lead.phone,
        lead.ip,
        lead.id
      ].join(' ').toLowerCase()
      
      if (!searchableText.includes(search)) return false
    }
    
    return true
  })
}